import { collection, documentId, orderBy, query, where } from 'firebase/firestore';
import type { Query, QueryConstraint } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import type { WorkJourneyFiltersState } from '@src/packages/workJourney/types';
import { expandDaysOfWeekForFirestoreQuery } from '@src/packages/utils/dates/formatDaysOfWeek';
import {
  chunkIds,
  FIRESTORE_IN_LIMIT,
} from '@src/packages/users/screens/UsersList/utils/buildUserQueries';
import { WORK_JOURNEY_FILTER_DEFAULTS } from '@src/packages/workJourney/workJourneyListFilterConfig';

export const hasWorkJourneyStateFilter = (
  state: WorkJourneyFiltersState['state'],
): boolean => Boolean(state && state !== WORK_JOURNEY_FILTER_DEFAULTS.STATE);

export const getWorkJourneyListFirestoreConstraints = (
  filters: Pick<WorkJourneyFiltersState, 'shift' | 'state' | 'dateRange'>,
): QueryConstraint[] => {
  const constraints: QueryConstraint[] = [];

  if (hasWorkJourneyStateFilter(filters.state)) {
    constraints.push(where('state', '==', filters.state));
  }

  if (filters.shift && filters.shift !== WORK_JOURNEY_FILTER_DEFAULTS.SHIFT) {
    constraints.push(where('workShift.id', '==', filters.shift));
  }

  if (filters.dateRange.length > 0) {
    const days = expandDaysOfWeekForFirestoreQuery(filters.dateRange);
    if (days.length > 0) {
      constraints.push(where('workShift.daysOfWeek', 'array-contains-any', days));
    }
  }

  return constraints;
};

/**
 * Builds Firestore realtime queries for the work journey list.
 * `ids === null` → collection query (no Elastic). Empty `ids` → no matches.
 * Does not combine `orderBy` with `documentId in` (Firestore rejects that combo).
 * State filters use equality only (no createdAt order) to avoid a composite index.
 */
export const buildWorkJourneyQueries = (
  orgId: string,
  ids: string[] | null,
  filters: Pick<WorkJourneyFiltersState, 'shift' | 'state' | 'dateRange'>,
): Query[] => {
  if (!orgId) {
    return [];
  }

  const baseCollection = collection(
    db,
    FSCollections.ORGANIZATIONS,
    orgId,
    FSCollections.WORK_JOURNEY_CONFIGS,
  );
  const firestoreConstraints = getWorkJourneyListFirestoreConstraints(filters);
  const shouldOrderByCreatedAt = !hasWorkJourneyStateFilter(filters.state);

  if (ids == null) {
    return [
      query(
        baseCollection,
        ...firestoreConstraints,
        ...(shouldOrderByCreatedAt ? [orderBy('createdAt', 'desc')] : []),
      ),
    ];
  }

  if (ids.length === 0) {
    return [];
  }

  return chunkIds(ids, FIRESTORE_IN_LIMIT).map(idChunk =>
    query(baseCollection, where(documentId(), 'in', idChunk), ...firestoreConstraints),
  );
};

export { FIRESTORE_IN_LIMIT };
